/*
  planningUtils.js — Shared constants, formatters, and date helpers for the
  Collection Planning module. Extracted from CollectionPlanningPage.jsx
  (Feb 2026 refactor to reduce file bloat).
*/
import { Clock, CheckCircle, Circle, AlertTriangle } from "lucide-react";

export const LOGO_URL = "https://customer-assets.emergentagent.com/job_valuebot/artifacts/etd4vs0l_Absolin_logo_Blue-HD.png";

export const BILLING_TYPES = ["Implementation", "Cloud", "AMC", "AI Tool", "Consultation", "Change Request", "SaaS Users", "Others"];

export const BILLING_FREQUENCIES = [
  { key: "one_time",  label: "One Time" },
  { key: "monthly",   label: "Monthly" },
  { key: "quarterly", label: "Quarterly" },
  { key: "annually",  label: "Annually" },
];

// Frequency whitelist per billing type. Missing key => all frequencies allowed.
// - Change Request: One-time only (each request is a discrete scope + fee).
// - SaaS Users: One-time (initial licensing) or Annual (subscription renewal).
export const BILLING_TYPE_ALLOWED_FREQUENCIES = {
  "Change Request": ["one_time"],
  "SaaS Users": ["one_time", "annually"],
};

// Given a type, returns the allowed BILLING_FREQUENCIES list (defaults to all).
export const allowedFrequenciesForType = (type) => {
  const keys = BILLING_TYPE_ALLOWED_FREQUENCIES[type];
  if (!keys) return BILLING_FREQUENCIES;
  return BILLING_FREQUENCIES.filter(f => keys.includes(f.key));
};

// Returns the correct default frequency to auto-set when a type's whitelist
// no longer includes the currently selected frequency.
export const defaultFrequencyForType = (type, current) => {
  const keys = BILLING_TYPE_ALLOWED_FREQUENCIES[type];
  if (!keys) return current || "one_time";
  if (keys.includes(current)) return current;
  return keys[0];
};

// Validate billing line start_date matches frequency rules (Indian FY quarters)
// Allows both period-start (1st) AND period-end (last day) dates.
const _lastDayOfMonth = (y, m) => new Date(y, m, 0).getDate(); // m is 1-based
export const validateBillingStart = (freq, start) => {
  if (!freq) return "";
  if (freq === "one_time") return "";
  if (!start) return `${freq[0].toUpperCase() + freq.slice(1)} lines require a start date.`;
  const m = start.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return "Invalid date format.";
  const yr = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const d = parseInt(m[3], 10);
  const lastDay = _lastDayOfMonth(yr, mo);
  if (freq === "monthly") {
    if (d !== 1 && d !== lastDay) return "Monthly billing must start on the 1st or last day of a month.";
    return "";
  }
  if (freq === "quarterly") {
    const startPairs = [[4,1],[7,1],[10,1],[1,1]];             // FY quarter starts
    const endPairs   = [[6,30],[9,30],[12,31],[3,31]];         // FY quarter ends
    if (!startPairs.some(([mm,dd]) => mm === mo && dd === d) &&
        !endPairs.some(([mm,dd]) => mm === mo && dd === d))
      return "Quarterly must fall on a quarter-start (Apr 1 / Jul 1 / Oct 1 / Jan 1) or quarter-end (Jun 30 / Sep 30 / Dec 31 / Mar 31).";
    return "";
  }
  if (freq === "annually") {
    const okApr1 = (mo === 4 && d === 1);
    const okMar31 = (mo === 3 && d === 31);
    if (!okApr1 && !okMar31) return "Annual billing must start on Apr 1 (FY start) or Mar 31 (FY end).";
    return "";
  }
  return "";
};

// Validate that end_date (optional) is >= start_date; also enforces same rule shape as start.
export const validateBillingEnd = (start, end, freq) => {
  if (!end) return "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(end)) return "Invalid end date.";
  if (start && end < start) return "End date cannot be before start date.";
  // If a frequency was supplied, allow only period-start or period-end shapes (mirrors start rule).
  if (freq && freq !== "one_time") {
    const shapeErr = validateBillingStart(freq, end);
    if (shapeErr) return shapeErr.replace(/must (start|fall)/i, "must end").replace("start", "end");
  }
  return "";
};

export const REVENUE_STREAMS = [
  { key: "erp",          label: "ERP",          color: "#0066CC" },
  { key: "odoo",         label: "Odoo",         color: "#7C3AED" },
  { key: "oquis",        label: "Oquis",        color: "#DB2777" },
  { key: "web_apps",     label: "Web Apps",     color: "#059669" },
  { key: "cloud_others", label: "Cloud/Others", color: "#D97706" },
];

export const STATUS_MAP = {
  planned: { label: "Planned", color: "bg-blue-100 text-blue-700", icon: Clock },
  collected: { label: "Collected", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  partial: { label: "Partial", color: "bg-amber-100 text-amber-700", icon: Circle },
  overdue: { label: "Overdue", color: "bg-red-100 text-red-700", icon: AlertTriangle },
};

export const fmt = (amt) => {
  if (!amt && amt !== 0) return "0";
  // Always render in Lakhs (₹L). Examples: 12500 -> 0.13 L, 250000 -> 2.50 L, 25000000 -> 250 L
  const lakhs = amt / 100000;
  if (lakhs >= 100) return `${Math.round(lakhs).toLocaleString("en-IN")} L`;
  if (lakhs >= 10) return `${lakhs.toFixed(1)} L`;
  return `${lakhs.toFixed(2)} L`;
};

// Indian-format full-rupee display: 65000 -> "₹65,000", 426000 -> "₹4,26,000"
export const fmtFull = (amt) => {
  if (!amt && amt !== 0) return "₹0";
  return `₹${Math.round(amt).toLocaleString("en-IN")}`;
};

// Strip non-digits & leading zeros so user-facing field never shows "02323"
export const sanitizeNumeric = (raw) => {
  if (raw === "" || raw == null) return "";
  const digits = String(raw).replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  return digits;
};

export const _fmtDate = (d) => d.toISOString().split("T")[0];
export const _addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };

export const _getProjectAmount = (p) =>
  p?.project_amount ? p.project_amount : (p?.one_time_charges || 0) + (p?.recurring_amount || 0) + (p?.cloud_server_charges || 0);

export function getDatePreset(preset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-indexed
  if (preset === "current_fy") {
    const fyStart = m >= 3 ? new Date(y, 3, 1) : new Date(y - 1, 3, 1);
    return { start: _fmtDate(fyStart), end: _fmtDate(now), label: m >= 3 ? `FY${String(y).slice(-2)}-${String(y + 1).slice(-2)}` : `FY${String(y - 1).slice(-2)}-${String(y).slice(-2)}` };
  }
  if (preset === "last_fy") {
    const fyEnd = m >= 3 ? new Date(y, 2, 31) : new Date(y - 1, 2, 31);
    const fyStart = m >= 3 ? new Date(y - 1, 3, 1) : new Date(y - 2, 3, 1);
    return { start: _fmtDate(fyStart), end: _fmtDate(fyEnd), label: `FY${String(fyStart.getFullYear()).slice(-2)}-${String(fyEnd.getFullYear()).slice(-2)}` };
  }
  if (preset === "current_month") return { start: _fmtDate(new Date(y, m, 1)), end: _fmtDate(now), label: now.toLocaleDateString("en-IN", { month: "long", year: "numeric" }) };
  if (preset === "previous_month") {
    const start = new Date(y, m - 1, 1);
    const end = new Date(y, m, 0);
    return { start: _fmtDate(start), end: _fmtDate(end), label: start.toLocaleDateString("en-IN", { month: "long", year: "numeric" }) };
  }
  if (preset === "current_quarter") {
    let qStartMonth;
    if (m >= 3 && m <= 5) qStartMonth = 3;
    else if (m >= 6 && m <= 8) qStartMonth = 6;
    else if (m >= 9 && m <= 11) qStartMonth = 9;
    else qStartMonth = 0;
    const start = new Date(qStartMonth === 0 ? y : y, qStartMonth, 1);
    if (qStartMonth === 0 && m < 3) start.setFullYear(y);
    return { start: _fmtDate(start), end: _fmtDate(now), label: ["Q4", "Q4", "Q4", "Q1", "Q1", "Q1", "Q2", "Q2", "Q2", "Q3", "Q3", "Q3"][m] };
  }
  if (preset === "previous_quarter") {
    let qStart, qEnd;
    if (m >= 3 && m <= 5) { qStart = new Date(y - 1, 0, 1); qEnd = new Date(y - 1, 2, 31); }
    else if (m >= 6 && m <= 8) { qStart = new Date(y, 3, 1); qEnd = new Date(y, 5, 30); }
    else if (m >= 9 && m <= 11) { qStart = new Date(y, 6, 1); qEnd = new Date(y, 8, 30); }
    else { qStart = new Date(y - 1, 9, 1); qEnd = new Date(y - 1, 11, 31); }
    return { start: _fmtDate(qStart), end: _fmtDate(qEnd), label: ["Q3", "Q3", "Q3", "Q4", "Q4", "Q4", "Q1", "Q1", "Q1", "Q2", "Q2", "Q2"][m] };
  }
  if (preset === "as_on_date") return { start: _fmtDate(now), end: _fmtDate(now), label: "Today" };
  if (preset === "today") return { start: _fmtDate(now), end: _fmtDate(now), label: "Today" };
  if (preset === "yesterday") {
    const yest = new Date(y, m, now.getDate() - 1);
    return { start: _fmtDate(yest), end: _fmtDate(yest), label: "Yesterday" };
  }
  if (preset === "current_week") {
    const dow = now.getDay();
    const diffToMonday = dow === 0 ? -6 : 1 - dow;
    const start = new Date(y, m, now.getDate() + diffToMonday);
    return { start: _fmtDate(start), end: _fmtDate(now), label: "Current Week" };
  }
  if (preset === "last_week") {
    const dow = now.getDay();
    const diffToMonday = dow === 0 ? -6 : 1 - dow;
    const thisMon = new Date(y, m, now.getDate() + diffToMonday);
    const lastMon = new Date(thisMon); lastMon.setDate(thisMon.getDate() - 7);
    const lastSun = new Date(thisMon); lastSun.setDate(thisMon.getDate() - 1);
    return { start: _fmtDate(lastMon), end: _fmtDate(lastSun), label: "Last Week" };
  }
  return { start: _fmtDate(now), end: _fmtDate(now), label: "Today" };
}

export const PRESETS = [
  { id: "current_fy",       label: "Current FY" },
  { id: "current_month",    label: "Current Month" },
  { id: "previous_month",   label: "Previous Month" },
  { id: "current_quarter",  label: "Current Quarter" },
  { id: "previous_quarter", label: "Previous Quarter" },
  { id: "last_fy",          label: "Last FY" },
  { id: "as_on_date",       label: "As on Date" },
];

export const RECEIPT_PRESETS = [
  { id: "today",        label: "Today" },
  { id: "yesterday",    label: "Yesterday" },
  { id: "current_week", label: "Current Week" },
  { id: "last_week",    label: "Last Week" },
];
